import { useState, useEffect } from "react";
import { reviewsApi } from "../lib";
import { showError } from "../lib/toast";
import {
  Star,
  Loader,
  MessageCircle,
  ThumbsUp,
  Filter,
  BookOpen,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

interface Review {
  _id: string;
  user: {
    _id: string;
    name: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
  };
  course: {
    _id: string;
    title: string;
    thumbnail?: string;
  };
  rating: number;
  comment: string;
  isApproved: boolean;
  helpful: number;
  createdAt: string;
}

interface RatingStat {
  courseId: string;
  courseTitle: string;
  courseThumbnail?: string;
  avgRating: number;
  totalReviews: number;
}

const InstructorReviewsPage = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [ratingStats, setRatingStats] = useState<RatingStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [sortBy, setSortBy] = useState("latest");

  useEffect(() => {
    fetchReviews();
  }, [page, selectedCourse, sortBy]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await reviewsApi.getInstructorReviews({
        page,
        limit: 15,
        courseId: selectedCourse || undefined,
        sort: sortBy,
      });

      if (response.success) {
        setReviews(response.data);
        setRatingStats(response.ratingStats || []);
        setTotal(response.pagination?.total || 0);
      }
    } catch (err: any) {
      console.error("Failed to fetch reviews:", err);
      showError("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const renderStars = (rating: number, size = "w-3 h-3") => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "text-gray-300"
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 shadow-sm">
              <MessageCircle className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-gray-900 md:text-xl">
              Course Reviews
            </h1>
          </div>
          <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
            Manage and monitor reviews for your courses
          </p>
        </div>

        {/* Rating Stats - Responsive grid */}
        {ratingStats.length > 0 && (
          <div className="grid grid-cols-1 gap-3 mb-5 sm:grid-cols-2 lg:grid-cols-3">
            {ratingStats.map((stat) => (
              <div
                key={stat.courseId}
                className="flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-all"
              >
                {stat.courseThumbnail ? (
                  <img
                    src={stat.courseThumbnail}
                    alt={stat.courseTitle}
                    className="object-cover w-12 h-12 rounded-lg"
                  />
                ) : (
                  <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg">
                    <BookOpen className="w-5 h-5 text-orange-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-semibold text-gray-800 truncate md:text-sm">
                    {stat.courseTitle}
                  </p>
                  <div className="flex flex-wrap items-center gap-1 mt-1">
                    <div className="flex items-center gap-0.5">
                      {renderStars(stat.avgRating, "w-2.5 h-2.5 md:w-3 md:h-3")}
                      <span className="ml-0.5 text-[10px] font-medium text-gray-700 md:text-xs">
                        {stat.avgRating.toFixed(1)}
                      </span>
                    </div>
                    <span className="text-[9px] text-gray-400 md:text-[10px]">
                      ({stat.totalReviews}{" "}
                      {stat.totalReviews === 1 ? "review" : "reviews"})
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filters - Mobile responsive */}
        <div className="mb-5 bg-white border border-gray-100 rounded-xl shadow-sm p-3 md:p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex-1">
              <select
                value={selectedCourse}
                onChange={(e) => {
                  setSelectedCourse(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 md:text-sm"
              >
                <option value="">All Courses</option>
                {ratingStats.map((stat) => (
                  <option key={stat.courseId} value={stat.courseId}>
                    {stat.courseTitle}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 md:text-sm"
              >
                <option value="latest">Latest</option>
                <option value="oldest">Oldest</option>
                <option value="highest">Highest Rating</option>
                <option value="lowest">Lowest Rating</option>
              </select>
            </div>
          </div>
        </div>

        {/* Reviews List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-6 h-6 text-orange-500 animate-spin" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="py-12 text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-gray-100 to-gray-200">
                <MessageCircle className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-[11px] text-gray-500 md:text-sm">
                No reviews found
              </p>
              <p className="text-[9px] text-gray-400 mt-1">
                Reviews will appear here once students leave feedback
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View - Hidden on mobile */}
              <div className="hidden md:block">
                <div className="divide-y divide-gray-100">
                  {reviews.map((review) => (
                    <div
                      key={review._id}
                      className="p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex gap-4">
                        {/* Course Thumbnail */}
                        {review.course.thumbnail ? (
                          <img
                            src={review.course.thumbnail}
                            alt={review.course.title}
                            className="object-cover w-14 h-14 rounded-lg"
                          />
                        ) : (
                          <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg">
                            <BookOpen className="w-6 h-6 text-orange-600" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          {/* User & Rating */}
                          <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2">
                              {review.user?.avatar ? (
                                <img
                                  src={review.user.avatar}
                                  alt={review.user?.name || "User"}
                                  className="w-6 h-6 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center">
                                  <span className="text-[10px] font-medium text-orange-700">
                                    {review.user?.firstName
                                      ?.charAt(0)
                                      .toUpperCase() ||
                                      review.user?.name
                                        ?.charAt(0)
                                        .toUpperCase() ||
                                      "?"}
                                  </span>
                                </div>
                              )}
                              <p className="text-xs font-medium text-gray-900">
                                {review.user?.firstName && review.user?.lastName
                                  ? `${review.user.firstName} ${review.user.lastName}`
                                  : review.user?.firstName ||
                                    review.user?.name ||
                                    "Anonymous"}
                              </p>
                              <p className="text-[10px] text-gray-400">
                                {formatDate(review.createdAt)}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              {renderStars(review.rating, "w-3 h-3")}
                            </div>
                          </div>

                          {/* Course Title */}
                          <p className="mb-1 text-[11px] font-medium text-orange-600">
                            {review.course.title}
                          </p>

                          {/* Comment */}
                          <p className="mb-2 text-xs text-gray-600 leading-relaxed">
                            {review.comment}
                          </p>

                          {/* Footer */}
                          <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-1 text-gray-400">
                              <ThumbsUp className="w-3 h-3" />
                              <span className="text-[10px]">
                                {review.helpful}{" "}
                                {review.helpful === 1
                                  ? "person found"
                                  : "people found"}{" "}
                                this helpful
                              </span>
                            </div>
                            {!review.isApproved && (
                              <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-[9px] font-medium rounded-full">
                                Pending Approval
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile Card View - Visible only on mobile */}
              <div className="divide-y divide-gray-100 md:hidden">
                {reviews.map((review) => (
                  <div
                    key={review._id}
                    className="p-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-2 mb-2">
                      {review.user?.avatar ? (
                        <img
                          src={review.user.avatar}
                          alt={review.user?.name || "User"}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center">
                          <span className="text-[10px] font-medium text-orange-700">
                            {review.user?.firstName?.charAt(0).toUpperCase() ||
                              review.user?.name?.charAt(0).toUpperCase() ||
                              "?"}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-gray-900">
                            {review.user?.firstName && review.user?.lastName
                              ? `${review.user.firstName} ${review.user.lastName}`
                              : review.user?.firstName ||
                                review.user?.name ||
                                "Anonymous"}
                          </p>
                          <div className="flex items-center gap-0.5">
                            {renderStars(review.rating, "w-2.5 h-2.5")}
                          </div>
                        </div>
                        <p className="text-[9px] text-gray-400 mt-0.5">
                          {formatDate(review.createdAt)}
                        </p>
                      </div>
                    </div>

                    {/* Course Info */}
                    <div className="flex items-center gap-2 mb-2 p-2 bg-gray-50 rounded-lg">
                      {review.course.thumbnail ? (
                        <img
                          src={review.course.thumbnail}
                          alt={review.course.title}
                          className="object-cover w-8 h-8 rounded"
                        />
                      ) : (
                        <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-orange-100 to-orange-200 rounded">
                          <BookOpen className="w-3.5 h-3.5 text-orange-600" />
                        </div>
                      )}
                      <p className="text-[10px] font-medium text-orange-600 truncate flex-1">
                        {review.course.title}
                      </p>
                    </div>

                    {/* Comment */}
                    <p className="text-[10px] text-gray-600 leading-relaxed mb-2">
                      {review.comment}
                    </p>

                    {/* Footer */}
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-1 text-gray-400">
                        <ThumbsUp className="w-2.5 h-2.5" />
                        <span className="text-[8px]">
                          {review.helpful} helpful
                        </span>
                      </div>
                      {!review.isApproved && (
                        <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-[8px] font-medium rounded-full">
                          Pending
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Pagination - Mobile responsive */}
          {total > 15 && (
            <div className="p-3 border-t border-gray-100 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[9px] text-gray-500 text-center sm:text-left md:text-xs">
                Showing {reviews.length} of {total} reviews
              </p>
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="flex items-center gap-0.5 px-2.5 py-1 text-[9px] font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors md:text-xs"
                >
                  <ChevronLeft className="w-3 h-3" />
                  Prev
                </button>
                <span className="text-[9px] text-gray-600 md:text-xs">
                  Page {page} of {Math.ceil(total / 15)}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page * 15 >= total}
                  className="flex items-center gap-0.5 px-2.5 py-1 text-[9px] font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors md:text-xs"
                >
                  Next
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Stats Summary Footer */}
        {reviews.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mt-5">
            <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
              <Star className="w-4 h-4 mx-auto mb-0.5 text-yellow-400 fill-yellow-400" />
              <p className="text-[10px] font-bold text-gray-900">
                {(
                  reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
                ).toFixed(1)}
              </p>
              <p className="text-[7px] text-gray-500">Avg Rating</p>
            </div>
            <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
              <ThumbsUp className="w-4 h-4 mx-auto mb-0.5 text-blue-500" />
              <p className="text-[10px] font-bold text-gray-900">
                {reviews.reduce((sum, r) => sum + r.helpful, 0)}
              </p>
              <p className="text-[7px] text-gray-500">Total Helpful</p>
            </div>
            <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
              <MessageCircle className="w-4 h-4 mx-auto mb-0.5 text-green-500" />
              <p className="text-[10px] font-bold text-gray-900">
                {reviews.filter((r) => !r.isApproved).length}
              </p>
              <p className="text-[7px] text-gray-500">Pending</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructorReviewsPage;
